import { filter, groupBy, isEmpty, isNumber, keys, reduce, map } from 'lodash'
import moment from 'moment'
import { store } from '../stores'
import { getCurrentEventId } from '../selectors/events'
import PaymentTypes from './paymentTypes'
import { centsToDollars } from '../utils/formatters'

export const MENU_TYPES = {
  preorder: 'Pre-Order',
  day_of_event: 'Day of Event',
  coffee: 'Coffee Cart',
  cordial: 'Cordial Cart',
  dessert: 'Dessert Cart',
  restock: 'Restock Order',
}

export const SHOULD_VISIT_MENU_TYPES = [ 'coffee', 'cordial', 'dessert' ]

const PREFERENCES_MAP = {
  flags: {
    type: 'Customer::FlagsPreference',
    attributes: ['lockRefrigerator', 'lockLiquorCabinet', 'presentBill', 'taxExempt'],
  },
  gratuity: {
    type: 'Customer::GratuityPreference',
    attributes: ['gratuity', 'gratuityIsAdjustable', 'gratuityIsPercentage', 'maxGratuity'],
  },
  notes: {
    type: 'Customer::NotesPreference',
    attributes: ['discountDescription', 'host', 'posNotes'],
  },
  payment: {
    type: 'Customer::PaymentPreference',
    attributes: [], // no attributes, just use this to grab customer_payment_id
  },
  receipt: {
    type: 'Customer::ReceiptPreference',
    attributes: ['provideReceipt', 'receiptEmail'],
  },
}

const booleanDisplay = (value, truthyDisplay, falseyDisplay) => value ? truthyDisplay : falseyDisplay

const gratuityDisplayString = (preference) => {
  const gratuity = preference.gratuity

  if (!isNumber(gratuity)) return ''
  if (gratuity < 1) return ''

  const isPercent = preference.gratuityIsPercentage

  let result = isPercent ? `${gratuity}%` : centsToDollars(gratuity)
  if (preference.gratuityIsAdjustable) result += ' (adjustable)'

  return result
}

const gratuityMaxDisplayString = (setting) => {
  const maxGratuity = setting.maxGratuity
  if (!isNumber(maxGratuity)) return ''

  return centsToDollars(maxGratuity)
}

const getLatestPref = (prefs) =>
  reduce(prefs, (mostRecentPref, pref) =>
    moment(pref?.updatedAt).isAfter(moment(mostRecentPref?.updatedAt))
      ? pref
      : mostRecentPref
  )


const getMostSpecificPreference = (preferences, currentEventId, preferenceType) => {
  if (isEmpty(preferences)) return {}

  let prefs = []

  prefs = filter(preferences, (pref) => pref.salesEventId === currentEventId)
  if (!isEmpty(prefs) || preferenceType === 'Customer::PaymentPreference') return getLatestPref(prefs) || {}

  prefs = filter(preferences, (pref) => !pref.salesEventId && pref.standUuid)
  if (!isEmpty(prefs)) return getLatestPref(prefs) || {}

  prefs = filter(preferences, (pref) => !pref.standUuid && !pref.salesEventId)
  return getLatestPref(prefs) || {}
}

const getMostSpecificMenuPreference = (preferences, menuType, currentEventId, preferenceType) => {
  if (isEmpty(preferences)) return {}

  let menuTypePrefs = filter(preferences, (pref) => pref.menuSubtype === menuType)

  // if no matches, want to also exclude preferences for other menu types
  if (isEmpty(menuTypePrefs)) menuTypePrefs = filter(preferences, (pref) => !pref.menuSubtype)

  return getMostSpecificPreference(menuTypePrefs, currentEventId, preferenceType)
}

const makeNonMenuPreferences = (groupedPreferences, currentEventId) => {
  if (isEmpty(groupedPreferences)) return {}

  const preferenceKeys = ['flags', 'notes', 'receipt']

  const combinedPreferences = reduce(preferenceKeys, (result, preferenceKey) => {
    const preferenceMap = PREFERENCES_MAP[preferenceKey]
    const preferenceGroup = groupedPreferences[preferenceMap.type]
    const nonMenuGroup = filter(preferenceGroup, ['menuSubtype', null])
    const mostSpecific = getMostSpecificPreference(nonMenuGroup, currentEventId)

    preferenceMap.attributes.map(attr => result[attr] = mostSpecific[attr])

    return result
  }, {})

  return combinedPreferences
}

const addPaymentDisplayToPayment = (payment) => {
  let display = payment.shortDescription

  if (PaymentTypes.isMobileWalletPayment(payment.paymentType)) display = display ?? 'Mobile Pay'
  // NOTE (westinschepper): This is temporary
  if (PaymentTypes.isDirectBill(payment.paymentType)) display = 'Payment Pending'
  if (PaymentTypes.isHouseAccount(payment.paymentType)) display = 'House Account'
  if (PaymentTypes.isFnbCredit(payment.paymentType)) display = 'F&B Credit'

  if (display) return { ...payment, paymentDisplay: display }

  return payment
}

const makeMenuPreferences = (groupedPreferences, payments, currentEventId) => {
  const menuTypes = keys(MENU_TYPES)
  const preferenceKeys = ['flags', 'gratuity', 'payment']

  const combinedPreferences = reduce(menuTypes, (result, menuType) => {
    result[menuType] = {}

    preferenceKeys.forEach((key) => {
      const preferenceMap = PREFERENCES_MAP[key]
      const preferenceGroup = groupedPreferences[preferenceMap.type]
      const mostSpecific = getMostSpecificMenuPreference(preferenceGroup, menuType, currentEventId, preferenceMap.type)

      let menuResult = {}

      if (key === 'gratuity') {
        menuResult = reduce(preferenceMap.attributes, (menuPrefs, attr) => {
          menuPrefs[attr] = mostSpecific[attr]

          return menuPrefs
        }, {})

        menuResult.gratuityIsAdjustableDisplay = booleanDisplay(menuResult.gratuityIsAdjustable, 'Yes', 'No')
        menuResult.gratuityDisplay = gratuityDisplayString(menuResult)
        menuResult.maxGratuityDisplay = gratuityMaxDisplayString(menuResult)
      }

      if (key === "payment") {
        menuResult.payments = map(
          mostSpecific?.customerPaymentIds,
          (customerPaymentId) =>
            addPaymentDisplayToPayment(
              payments.find((payment) => payment.uuid === customerPaymentId) ||
                {}
            )
        )
      }

      if (key === 'flags') menuResult.shouldVisit = mostSpecific.shouldVisit

      result[menuType] = { ...result[menuType], ...menuResult }
    })

    return result
  }, {})
  return combinedPreferences
}

export const makeCustomerPreferences = (preferences, payments) => {
  const byType = groupBy(preferences, 'type')
  const state = store.getState()
  const currentEventId = getCurrentEventId(state)

  const nonMenuPreferences = makeNonMenuPreferences(byType, currentEventId)
  const menuPreferences = makeMenuPreferences(byType, payments, currentEventId)

  return {
    ...nonMenuPreferences,
    ...menuPreferences,
    lockRefrigerator: booleanDisplay(nonMenuPreferences.lockRefrigerator, 'Closed', 'Open' ),
    lockLiquorCabinet: booleanDisplay(nonMenuPreferences.lockLiquorCabinet, 'Closed', 'Open' ),
    presentBill: booleanDisplay(nonMenuPreferences.presentBill, 'Yes', 'No' ),
    provideReceipt: booleanDisplay(nonMenuPreferences.provideReceipt, 'Yes', 'No' ),
    taxExempt: booleanDisplay(nonMenuPreferences.taxExempt, 'Yes', 'No'),
    taxExemptBool: nonMenuPreferences.taxExempt,
  }
}

export default makeCustomerPreferences
